import axios from 'axios'

export interface SubmittedAddressData {
  ownVatId: string
  requestedVatId: string
  companyName: string
  street: string
  zip: string
  city: string
  country: string
}

export interface ValidateAddressPayload {
  vatId?: string
  requestedVatId?: string
  ownVatId?: string
  companyName?: string
  street?: string
  zip?: string
  city?: string
  country?: string
}

export interface ValidateTransactionPayload {
  vatId?: string
  requestedVatId?: string
  ownVatId?: string
  companyName?: string
  street?: string
  zip?: string
  city?: string
  country?: string
  addressNr?: string
  transactionType?: string
}

export interface ValidationHistoryItem {
  validationId: string
  timestamp: string
  trigger: string
  validationType?: string
  interface?: string
  status: string
  statusDisplay: string
  valid: boolean
  officialStatusCode?: string
  companyNameMatch?: string
  streetMatch?: string
  zipMatch?: string
  cityMatch?: string
  allMatchesCodeA?: boolean
  transactionType?: string
  transactionNo?: string
  reusedFromValidationId?: string
}

export interface AddressStatusSummary {
  customer: string
  tenant: string
  addressNr: string
  lastCheckTimestamp: string
  lastValidationId: string
  validationType?: string
  interface?: string
  status: string
  statusDisplay: string
  testedVatId: string
  valid: boolean
  snapshotHash: string
  submittedData: SubmittedAddressData
  history: ValidationHistoryItem[]
}

export interface AuditResult {
  valid: boolean
  companyNameMatch?: string
  streetMatch?: string
  zipMatch?: string
  cityMatch?: string
  officialStatusCode?: string
  officialMessage?: string
  officialRequestId?: string
  validFrom?: string
  validTill?: string
}

export interface AuditProtocol {
  validationId: string
  customer: string
  tenant: string
  addressNr: string
  transactionType?: string
  transactionNo?: string
  timestamp: string
  trigger: string
  validationType: string
  interface: string
  status: string
  statusDisplay: string
  submittedData: SubmittedAddressData
  result: AuditResult
  rawResponse?: Record<string, unknown>
  reusedFromValidationId?: string
}

export interface TransactionStatusSummary {
  customer: string
  tenant: string
  transactionNr: string
  transactionType: string
  addressNr: string
  vatId: string
  validationId: string
  timestamp: string
  validationType?: string
  interface?: string
  status: string
  statusDisplay: string
  valid: boolean
  reused: boolean
  reusedFromValidationId?: string
  latestAddressValidation?: AddressStatusSummary
  history?: ValidationHistoryItem[]
}

export const vatValidationApi = {
  getAddressStatus: (addressNr: string, tenant?: string, customer?: string) => {
    return axios.get<AddressStatusSummary>(`/api/vat-validation/v1/addresses/${addressNr}`, {
      params: { tenant, customer },
    })
  },

  getAddressStatuses: (addressNrs?: string[], tenant?: string, customer?: string) => {
    if (addressNrs && addressNrs.length > 0) {
      return axios.post<Record<string, AddressStatusSummary>>(
        '/api/vat-validation/v1/address-statuses',
        { addressNrs },
        { params: { tenant, customer } },
      )
    }
    return axios.get<Record<string, AddressStatusSummary>>(
      '/api/vat-validation/v1/address-statuses',
      { params: { tenant, customer } },
    )
  },

  getAuditProtocol: (
    addressNr: string,
    validationId: string,
    tenant?: string,
    customer?: string,
  ) => {
    return axios.get<AuditProtocol>(
      `/api/vat-validation/v1/addresses/${addressNr}/validations/${validationId}`,
      {
        params: { tenant, customer },
      },
    )
  },

  validateAddress: (
    addressNr: string,
    payload?: ValidateAddressPayload,
    tenant?: string,
    customer?: string,
  ) => {
    return axios.post<AuditProtocol>(
      `/api/vat-validation/v1/addresses/${addressNr}/validate`,
      payload || {},
      {
        params: { tenant, customer },
      },
    )
  },

  getTransactionStatus: (transactionNr: string, tenant?: string, customer?: string) => {
    return axios.get<TransactionStatusSummary>(
      `/api/vat-validation/v1/transactions/${transactionNr}`,
      {
        params: { tenant, customer },
      },
    )
  },

  validateTransaction: (
    transactionNr: string,
    payload?: ValidateTransactionPayload,
    tenant?: string,
    customer?: string,
  ) => {
    return axios.post<TransactionStatusSummary>(
      `/api/vat-validation/v1/transactions/${transactionNr}/validate`,
      payload || {},
      {
        params: { tenant, customer },
      },
    )
  },

  triggerBatchValidation: (
    scopeOrTenant: 'domestic' | 'foreign' | 'all' | string = 'all',
    tenantOrCustomer?: string,
    customerOrForceAll?: string | boolean,
    forceAll = true,
  ) => {
    let scope = 'all'
    let tenant: string | undefined
    let customer: string | undefined
    let force = forceAll

    if (scopeOrTenant === 'domestic' || scopeOrTenant === 'foreign' || scopeOrTenant === 'all') {
      scope = scopeOrTenant
      tenant = tenantOrCustomer
      customer = typeof customerOrForceAll === 'string' ? customerOrForceAll : undefined
      force = typeof customerOrForceAll === 'boolean' ? customerOrForceAll : forceAll
    } else {
      tenant = scopeOrTenant
      customer = tenantOrCustomer
      if (typeof customerOrForceAll === 'boolean') {
        force = customerOrForceAll
      }
    }

    // Always async: a full run is limited by BZSt (2 req/s) and would exceed the HTTP request timeout.
    return axios.post<{ status: string; checkedCount?: number; message: string; scope?: string }>(
      '/api/vat-validation/v1/addresses/batch-validate',
      { forceAll: force, scope, async: true },
      { params: { tenant, customer } },
    )
  },
}
